import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { dashboardAPI as apiConfig } from '../../../axiosConfig';
import { getEncodedURL } from '../../../utils/utils';
import dayjs from 'dayjs';

const URL =
  'payment-options-proxy?endpoint=paymentdashboard/legacy/contextual-message';

export const getLegacyContextualMessages = createAsyncThunk(
  'getLegacyContextualMessages',
  async (fetchFromDB = false, { rejectWithValue }) => {
    try {
      const url = getEncodedURL(`${URL}`, {
        ...(fetchFromDB && { source: 'DB' }),
        origin: 'DASHBOARD',
      });
      const { data, status } = await apiConfig.get(url);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getLegacyContextualMessagesById = createAsyncThunk(
  'getLegacyContextualMessagesById',
  async (id, { rejectWithValue }) => {
    try {
      const url = getEncodedURL(`${URL}/${id}`, {
        origin: 'DASHBOARD',
      });
      const { data, status } = await apiConfig.get(url);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteLegacyContextualMessages = createAsyncThunk(
  'deleteLegacyContextualMessages',
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${URL}`;
      const { data, status } = await apiConfig.delete(url, { data: payload });

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createLegacyContextualMessages = createAsyncThunk(
  'createLegacyContextualMessages',
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${URL}`;
      const { data, status } = await apiConfig.post(url, payload);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateLegacyContextualMessages = createAsyncThunk(
  'updateLegacyContextualMessages',
  async ({ payload, id }, { rejectWithValue }) => {
    try {
      const url = `${URL}/${id}`;
      const { data, status } = await apiConfig.put(url, payload);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const SLICE_NAME = 'legacyPaymentOptionsContextualMessages';

const initialState = {
  contextualMessages: [],
  selectedContextualMessage: null,
  contextualMessagesStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',
};

const legacyPaymentOptionsContextualMessages = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLegacyContextualMessages.pending, (state) => {
        state.contextualMessagesStatus = 'loading';
      })
      .addCase(getLegacyContextualMessages.rejected, (state) => {
        state.contextualMessagesStatus = 'failed';
      })
      .addCase(getLegacyContextualMessages.fulfilled, (state, { payload }) => {
        state.contextualMessages = payload;
        state.contextualMessagesStatus = 'succeeded';
      })
      .addCase(getLegacyContextualMessagesById.rejected, (state) => {
        state.selectedContextualMessage = null;
      })
      .addCase(
        getLegacyContextualMessagesById.fulfilled,
        (state, { payload }) => {
          state.selectedContextualMessage = payload ? payload : null;
        },
      );
  },
});

export const legacyPaymentOptionsContextualMessageActions =
  legacyPaymentOptionsContextualMessages.actions;

export const selectLegacyContextualMessages = (state) =>
  state.legacyPaymentOption.legacyContextualMessages.contextualMessages;

export const selectIsLoadingLegacyContextualMessages = (state) =>
  state.legacyPaymentOption.legacyContextualMessages
    .contextualMessagesStatus === 'loading';

export const selectSelectedContextualMessage = (state) =>
  state.legacyPaymentOption.legacyContextualMessages.selectedContextualMessage;

export default legacyPaymentOptionsContextualMessages.reducer;
